import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Send, Loader2, Paperclip, X, FileText } from 'lucide-react';
import toast from 'react-hot-toast';
import { useDebounce } from '../../hooks/useDebounce';
import { searchIssues, type SearchIssueResult } from '../../services/issueService';
import { uploadFile, type ChatAttachment, type MentionData } from '../../services/chatChannelService';
import MentionDropdown from './MentionDropdown';
import Spinner from '../ui/Spinner';

// ─── 이슈 멘션 자동완성 드롭다운 ───
function IssueAutocomplete({
  projectId, query, onSelect, onClose,
}: {
  projectId: string; query: string;
  onSelect: (issueKey: string) => void; onClose: () => void;
}) {
  const [results, setResults] = useState<SearchIssueResult[]>([]);
  const [loading, setLoading] = useState(false);
  const debouncedQuery = useDebounce(query, 300);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!debouncedQuery) { setResults([]); return; }
    let cancelled = false;
    setLoading(true);
    searchIssues(projectId, debouncedQuery)
      .then((data) => { if (!cancelled) setResults(data.slice(0, 5)); })
      .catch(() => { if (!cancelled) setResults([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [projectId, debouncedQuery]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) onClose();
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  if (!query && results.length === 0) return null;

  return (
    <div ref={containerRef}
      className="absolute bottom-full left-0 right-0 mb-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-52 overflow-y-auto z-10">
      {loading && <div className="flex items-center justify-center py-3"><Spinner className="size-4" /></div>}
      {!loading && results.length === 0 && debouncedQuery && (
        <div className="px-3 py-2 text-sm text-gray-400">검색 결과 없음</div>
      )}
      {results.map((issue) => (
        <button key={issue.id}
          className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-2 transition-colors"
          onMouseDown={(e) => { e.preventDefault(); onSelect(issue.issueId); }}>
          <span className="text-sm font-medium text-primary-600 shrink-0">{issue.issueId}</span>
          <span className="text-sm text-gray-700 truncate">{issue.title}</span>
        </button>
      ))}
    </div>
  );
}

const IMAGE_TYPES = new Set(['image/png', 'image/jpeg', 'image/gif', 'image/webp']);
const FILE_ACCEPT = 'image/*,.pdf,.docx,.xlsx,.pptx,.txt,.zip';

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

interface PendingFile {
  file: File;
  preview?: string;
  uploading: boolean;
  uploaded?: ChatAttachment;
  error?: boolean;
}

interface ChannelMember {
  userId: string;
  name: string;
  avatarUrl?: string | null;
}

// ─── 채널별 초안 저장소 (구독 가능) ───
interface DraftData {
  content: string;
  mentions: MentionData[];
}
const draftMap = new Map<string, DraftData>();
const draftListeners = new Set<() => void>();
let draftVersion = 0;

function notifyDraftChange() {
  draftVersion++;
  draftListeners.forEach((cb) => cb());
}

function setDraft(channelId: string, data: DraftData) {
  draftMap.set(channelId, data);
  notifyDraftChange();
}

function deleteDraft(channelId: string) {
  if (draftMap.has(channelId)) {
    draftMap.delete(channelId);
    notifyDraftChange();
  }
}

export function getDraft(channelId: string): DraftData | undefined {
  return draftMap.get(channelId);
}

export function clearDraft(channelId: string): void {
  deleteDraft(channelId);
}

/** Hook to subscribe to draft changes — triggers re-render when any draft changes */
export function useDraftVersion(): number {
  const [, setVer] = useState(draftVersion);
  useEffect(() => {
    const cb = () => setVer(draftVersion);
    draftListeners.add(cb);
    return () => { draftListeners.delete(cb); };
  }, []);
  return draftVersion;
}

// ─── 채팅 입력 영역 ───
export default function ChatInput({
  projectId, onSend, sending, disabled, compact, channelId, onTyping, onStopTyping, placeholder,
  channelMembers, currentUserRole, currentUserId,
}: {
  projectId: string;
  onSend: (content: string, attachments?: ChatAttachment[], mentions?: MentionData[]) => void;
  sending: boolean;
  disabled?: boolean;
  compact?: boolean;
  channelId?: string;
  onTyping?: () => void;
  onStopTyping?: () => void;
  placeholder?: string;
  channelMembers?: ChannelMember[];
  currentUserRole?: string | null;
  currentUserId?: string;
}) {
  const [content, setContent] = useState('');
  const [issueQuery, setIssueQuery] = useState<string | null>(null);
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // @ mention state
  const [userMentions, setUserMentions] = useState<MentionData[]>([]);
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const [mentionSelectedIdx, setMentionSelectedIdx] = useState(0);

  // 채널별 초안 저장/복원
  const prevChannelRef = useRef<string | undefined>(channelId);
  useEffect(() => {
    const prevCh = prevChannelRef.current;
    // 이전 채널 초안 저장
    if (prevCh && prevCh !== channelId) {
      const trimmed = content.trim();
      if (trimmed || userMentions.length > 0) {
        setDraft(prevCh, { content, mentions: userMentions });
      } else {
        deleteDraft(prevCh);
      }
    }
    // 새 채널 초안 복원
    const draft = channelId ? draftMap.get(channelId) : undefined;
    setContent(draft?.content ?? '');
    setUserMentions(draft?.mentions ?? []);
    setIssueQuery(null);
    setShowMentionDropdown(false);
    setPendingFiles([]);
    prevChannelRef.current = channelId;
  }, [channelId]); // eslint-disable-line react-hooks/exhaustive-deps

  // 언마운트 시 현재 초안 저장
  const contentRef = useRef(content);
  const mentionsRef = useRef(userMentions);
  contentRef.current = content;
  mentionsRef.current = userMentions;
  useEffect(() => {
    return () => {
      if (channelId) {
        const trimmed = contentRef.current.trim();
        if (trimmed || mentionsRef.current.length > 0) {
          setDraft(channelId, { content: contentRef.current, mentions: mentionsRef.current });
        } else {
          deleteDraft(channelId);
        }
      }
    };
  }, [channelId]);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  }, [content]);

  // Cleanup object URLs
  useEffect(() => {
    return () => {
      pendingFiles.forEach((f) => { if (f.preview) URL.revokeObjectURL(f.preview); });
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Filtered members for @ mention dropdown
  const filteredMention = useMemo(() => {
    if (!channelMembers) return { members: [] as ChannelMember[], specials: [] as { id: string; name: string; description: string }[] };
    const q = mentionSearch.toLowerCase();
    const members = channelMembers
      .filter((m) => m.userId !== currentUserId && m.name.toLowerCase().includes(q))
      .slice(0, 8);

    const specials: { id: string; name: string; description: string }[] = [];
    if (currentUserRole === 'OWNER' || currentUserRole === 'ADMIN') {
      if ('all'.startsWith(q) || !q) specials.push({ id: '@all', name: 'all', description: '전체 멤버 알림' });
      if ('here'.startsWith(q) || !q) specials.push({ id: '@here', name: 'here', description: '온라인 멤버 알림' });
    }
    return { members, specials };
  }, [channelMembers, mentionSearch, currentUserRole]);

  const totalMentionItems = filteredMention.members.length + filteredMention.specials.length;

  const addFiles = (files: FileList | File[]) => {
    const newFiles = Array.from(files).slice(0, 5 - pendingFiles.length);
    const pending: PendingFile[] = newFiles.map((file) => ({
      file,
      preview: IMAGE_TYPES.has(file.type) ? URL.createObjectURL(file) : undefined,
      uploading: false,
    }));
    setPendingFiles((prev) => [...prev, ...pending].slice(0, 5));
  };

  const removeFile = (index: number) => {
    setPendingFiles((prev) => {
      const removed = prev[index];
      if (removed?.preview) URL.revokeObjectURL(removed.preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  // Detect @ and # triggers
  const detectTriggers = useCallback((val: string, cursorPos: number) => {
    const textBeforeCursor = val.slice(0, cursorPos);

    // Detect # for issue mention
    const hashMatch = textBeforeCursor.match(/#(\S*)$/);
    setIssueQuery(hashMatch ? hashMatch[1] : null);

    // Detect @ for user mention
    const atMatch = textBeforeCursor.match(/@(\S*)$/);
    if (atMatch) {
      // Check that @ is at start or preceded by whitespace/newline
      const atIdx = textBeforeCursor.lastIndexOf('@');
      if (atIdx === 0 || /[\s\n]/.test(val[atIdx - 1])) {
        setShowMentionDropdown(true);
        setMentionSearch(atMatch[1]);
        setMentionSelectedIdx(0);
        return;
      }
    }
    setShowMentionDropdown(false);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    if (val.length > 2000) return;
    setContent(val);
    onTyping?.();
    detectTriggers(val, e.target.selectionStart);

    // Validate existing mentions (remove if text changed at that position)
    setUserMentions((prev) => prev.filter((m) => {
      const mentionText = `@${m.name}`;
      return val.substring(m.offset, m.offset + m.length) === mentionText;
    }));
  };

  const handleMentionSelect = useCallback((userId: string, name: string) => {
    const el = textareaRef.current;
    if (!el) return;
    const cursorPos = el.selectionStart;
    const textBeforeCursor = content.slice(0, cursorPos);
    const atIdx = textBeforeCursor.lastIndexOf('@');
    if (atIdx < 0) return;

    const mentionText = `@${name}`;
    const newContent = content.slice(0, atIdx) + mentionText + ' ' + content.slice(cursorPos);

    const newMention: MentionData = { userId, name, offset: atIdx, length: mentionText.length };

    // Adjust offsets of existing mentions after insertion point
    const insertedLength = (atIdx + mentionText.length + 1) - cursorPos;
    setUserMentions((prev) => [
      ...prev.map((m) => m.offset >= atIdx ? { ...m, offset: m.offset + insertedLength } : m),
      newMention,
    ]);

    setContent(newContent);
    setShowMentionDropdown(false);

    requestAnimationFrame(() => {
      const newPos = atIdx + mentionText.length + 1;
      el.setSelectionRange(newPos, newPos);
      el.focus();
    });
  }, [content]);

  const justSelectedMentionRef = useRef(false);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // IME 조합 중에는 키 이벤트 무시 (한글 입력 등)
    if (e.nativeEvent.isComposing) return;

    // 멘션 선택 직후 Enter 무시 (IME 확정 Enter와 구분)
    if (justSelectedMentionRef.current) {
      justSelectedMentionRef.current = false;
      if (e.key === 'Enter') { e.preventDefault(); return; }
    }

    // @ mention keyboard navigation
    if (showMentionDropdown && totalMentionItems > 0) {
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMentionSelectedIdx((p) => (p - 1 + totalMentionItems) % totalMentionItems);
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMentionSelectedIdx((p) => (p + 1) % totalMentionItems);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        const idx = mentionSelectedIdx;
        const { members, specials } = filteredMention;
        if (idx < members.length) {
          handleMentionSelect(members[idx].userId, members[idx].name);
        } else {
          const sIdx = idx - members.length;
          if (sIdx < specials.length) handleMentionSelect(specials[sIdx].id, specials[sIdx].name);
        }
        justSelectedMentionRef.current = true;
        return;
      }
      if (e.key === 'Escape') {
        setShowMentionDropdown(false);
        return;
      }
    }

    if (e.key === 'Escape') { setIssueQuery(null); setShowMentionDropdown(false); return; }
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const handleSend = async () => {
    const trimmed = content.trim();
    const hasFiles = pendingFiles.length > 0;
    if ((!trimmed && !hasFiles) || sending || disabled || uploading) return;

    // Collect valid mentions
    const finalMentions = userMentions.length > 0 ? userMentions : undefined;

    if (hasFiles && channelId) {
      setUploading(true);
      try {
        const attachments: ChatAttachment[] = [];
        for (const pf of pendingFiles) {
          if (pf.uploaded) { attachments.push(pf.uploaded); continue; }
          const result = await uploadFile(channelId, pf.file);
          attachments.push(result);
        }
        onSend(trimmed, attachments, finalMentions);
      } catch {
        toast.error('파일 업로드에 실패했습니다.');
        setUploading(false);
        return;
      }
      setUploading(false);
    } else {
      onSend(trimmed, undefined, finalMentions);
    }

    setContent('');
    setIssueQuery(null);
    setShowMentionDropdown(false);
    setUserMentions([]);
    setPendingFiles([]);
    if (channelId) deleteDraft(channelId); // 전송 후 초안 삭제
    onStopTyping?.();
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
  };

  const handleIssueMentionSelect = (issueKey: string) => {
    const el = textareaRef.current;
    if (!el) return;
    const cursorPos = el.selectionStart;
    const textBefore = content.slice(0, cursorPos);
    const textAfter = content.slice(cursorPos);
    const hashIndex = textBefore.lastIndexOf('#');
    if (hashIndex >= 0) {
      const newContent = textBefore.slice(0, hashIndex) + `#${issueKey}` + ' ' + textAfter;
      setContent(newContent);
      setIssueQuery(null);
      requestAnimationFrame(() => {
        const newPos = hashIndex + issueKey.length + 2;
        el.setSelectionRange(newPos, newPos);
        el.focus();
      });
    }
  };

  return (
    <div className={`relative border-t border-gray-200 bg-white shrink-0 min-h-[56px] ${compact ? 'p-2' : 'px-3 py-2'}`}>
      {issueQuery !== null && projectId && (
        <IssueAutocomplete projectId={projectId} query={issueQuery}
          onSelect={handleIssueMentionSelect} onClose={() => setIssueQuery(null)} />
      )}

      {showMentionDropdown && channelMembers && (
        <MentionDropdown
          members={filteredMention.members}
          specials={filteredMention.specials}
          selectedIndex={mentionSelectedIdx}
          onSelect={handleMentionSelect}
        />
      )}

      {/* Pending files preview */}
      {pendingFiles.length > 0 && (
        <div className="flex gap-2 mb-2 overflow-x-auto pb-1">
          {pendingFiles.map((pf, i) => (
            <div key={i} className="relative flex-shrink-0">
              {pf.preview ? (
                <img src={pf.preview} alt="" className="h-16 w-auto rounded-lg object-cover border" />
              ) : (
                <div className="h-16 w-32 bg-gray-50 border rounded-lg flex items-center gap-2 px-3">
                  <FileText className="size-4 text-gray-400 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-[11px] text-gray-700 truncate">{pf.file.name}</p>
                    <p className="text-[9px] text-gray-400">{formatSize(pf.file.size)}</p>
                  </div>
                </div>
              )}
              <button onClick={() => removeFile(i)}
                className="absolute -top-1.5 -right-1.5 bg-gray-700 text-white rounded-full size-4 flex items-center justify-center text-[11px] hover:bg-gray-900">
                <X className="size-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-end gap-2">
        {channelId && (
          <>
            <button onClick={() => fileInputRef.current?.click()} disabled={disabled || pendingFiles.length >= 5}
              className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-30 transition-colors shrink-0">
              <Paperclip className="size-5" />
            </button>
            <input ref={fileInputRef} type="file" hidden multiple accept={FILE_ACCEPT}
              onChange={(e) => { if (e.target.files) addFiles(e.target.files); e.target.value = ''; }} />
          </>
        )}
        <textarea ref={textareaRef} value={content} onChange={handleChange} onKeyDown={handleKeyDown}
          placeholder={placeholder ?? '메시지를 입력하세요...'} rows={1} maxLength={2000} disabled={disabled}
          className={`flex-1 resize-none border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none disabled:opacity-50 ${compact ? 'px-2.5 py-1.5 text-sm' : 'px-3 py-2 text-sm'}`} />
        <button onClick={handleSend}
          disabled={(!content.trim() && pendingFiles.length === 0) || sending || disabled || uploading}
          className="p-2 text-blue-500 hover:text-blue-600 disabled:text-gray-300 disabled:cursor-not-allowed transition-colors shrink-0">
          {(sending || uploading) ? <Loader2 className="size-5 animate-spin" /> : <Send className="size-5" />}
        </button>
      </div>
    </div>
  );
}
